import {
  Box,
  Flex,
  Icon,
  Text,
  useColorModeValue,
  Spinner,
  Center,
  ButtonGroup,
  Button,
  Switch,
  FormControl,
  FormLabel,
  HStack,
} from "@chakra-ui/react";
import Card from "components/card/Card.js";
import LineChart from "components/charts/LineChart";
import React, { useEffect, useState } from "react";
import { MdBarChart, MdOutlineCalendarToday, MdAddCircleOutline } from "react-icons/md";
import { RiArrowUpSFill } from "react-icons/ri";
import axios from "axios";
import { API_URL, CURRENCY } from "../../../../constants";
import { getAuthToken } from "../../../../helpers/helpers";

export const lineChartOptionsProperime = {
  chart: {
    toolbar: {
      show: false,
    },
    dropShadow: {
      enabled: true,
      top: 13,
      left: 0,
      blur: 10,
      opacity: 0.1,
      color: "#4318FF",
    },
  },
  colors: ["#4318FF"],
  markers: {
    size: 0,
    colors: "white",
    strokeColors: "#7551FF",
    strokeWidth: 3,
    strokeOpacity: 0.9,
    strokeDashArray: 0,
    fillOpacity: 1,
    discrete: [],
    shape: "circle",
    radius: 2,
    offsetX: 0,
    offsetY: 0,
    showNullDataPoints: true,
  },
  tooltip: {
    theme: "dark",
  },
  dataLabels: {
    enabled: false,
  },
  stroke: {
    curve: "smooth",
    type: "line",
  },
  xaxis: {
    type: "datetime",
    categories: [],
    labels: {
      style: {
        colors: "#A3AED0",
        fontSize: "12px",
        fontWeight: "500",
      },
    },
    axisBorder: {
      show: false,
    },
    axisTicks: {
      show: false,
    },
  },
  yaxis: {
    show: true,
  },
  legend: {
    show: false,
  },
  grid: {
    show: true,
    column: {
      color: ["#7551FF"],
      opacity: 0.5,
    },
  },
  color: ["#7551FF"],
};

export const lineChartOptionsVenteExtra = {
  ...lineChartOptionsProperime,
  colors: ["#01B574"],
  markers: {
    ...lineChartOptionsProperime.markers,
    strokeColors: "#01B574",
  },
  grid: {
    show: true,
    column: {
      color: ["#01B574"],
      opacity: 0.5,
    },
  },
  color: ["#01B574"],
  chart: {
    ...lineChartOptionsProperime.chart,
    dropShadow: {
      ...lineChartOptionsProperime.chart.dropShadow,
      color: "#01B574",
    },
  },
};

export default function ProperimeAndExtraSalesChart({ selectedDate, ...rest }) {
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const iconColor = useColorModeValue("brand.500", "white");
  const bgButton = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
  const bgButtonActive = useColorModeValue("brand.500", "brand.400");
  const textButtonActive = useColorModeValue("white", "white");
  const textButton = useColorModeValue("secondaryGray.900", "white");

  const [activeChart, setActiveChart] = useState("properime"); // "properime" or "venteExtra"
  const [showMonetary, setShowMonetary] = useState(false);

  // Pro-périmés data
  const [properimeCount, setProperimeCount] = useState(0);
  const [properimeValue, setProperimeValue] = useState(0);
  const [dailyProperimeSold, setDailyProperimeSold] = useState([]);

  // Vente Extra data
  const [venteExtraCount, setVenteExtraCount] = useState(0);
  const [venteExtraValue, setVenteExtraValue] = useState(0);
  const [dailyVenteExtraSold, setDailyVenteExtraSold] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  // Full cart data (cached) to avoid refetching when filtering by profile
  const [fullCart, setFullCart] = useState([]);
  const [selectedOperateur, setSelectedOperateur] = useState("all");
  const [uniqueOperateurs, setUniqueOperateurs] = useState([]);
  const [operatorNames, setOperatorNames] = useState({});
  const [isLoadingOperators, setIsLoadingOperators] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const token = getAuthToken();

      try {
        const response = await axios.get(`${API_URL}/api/cart`, {
          headers: {
            "x-access-token": token,
            Authorization: `Bearer ${token}`,
          },
        });

        const cartData = response.data;
        // Cache full cart so we can filter client-side without re-calling the API
        setFullCart(cartData);

        // Also fetch track actions to find additional operator codes (same logic as Utilisations)
        const responseTrack = await axios
          .get(`${API_URL}/api/track/action/AFFICHER_TRAITEMENT`, {
            headers: { "x-access-token": token, Authorization: `Bearer ${token}` },
          })
          .catch(() => ({ data: [] }));

        const operateursFromRes1 = (responseTrack.data || []).map((item) => item.code_operateur).filter(Boolean);
        const operateursFromRes2 = (cartData || []).map((item) => item.code_operateur).filter(Boolean);
        const allOperateurs = [...new Set([...(operateursFromRes1 || []), ...(operateursFromRes2 || [])])];
        setUniqueOperateurs(allOperateurs);

        // Fetch operator display names
        const fetchOperatorNames = async (codeOperateur) => {
          try {
            const token = getAuthToken();
            const userId = localStorage.getItem("UserID");

            const resp = await axios.post(
              `${API_URL}/api/profile/${userId}`,
              { code_operateur: codeOperateur },
              {
                headers: { "x-access-token": token, Authorization: `Bearer ${token}` },
              }
            );

            return resp.data.nom_operateur;
          } catch (err) {
            return null;
          }
        };

        const fetchAllOperatorNames = async () => {
          setIsLoadingOperators(true);
          const names = {};
          for (const code of allOperateurs) {
            const nom = await fetchOperatorNames(code);
            if (nom) names[code] = nom;
          }
          setOperatorNames(names);
          setIsLoadingOperators(false);
        };

        fetchAllOperatorNames();
        const properimeSoldPerDay = {};
        const properimeValuePerDay = {};
        const venteExtraSoldPerDay = {};
        const venteExtraValuePerDay = {};

        function isNearExpiry(saleDate, expirationDate, nearExpiryMonths) {
          const d1 = saleDate instanceof Date ? saleDate : new Date(saleDate);
          const d2 = expirationDate instanceof Date ? expirationDate : new Date(expirationDate);
          const months = (nearExpiryMonths != null) ? nearExpiryMonths : 6;

          const monthDiff =
            (d2.getFullYear() - d1.getFullYear()) * 12 +
            (d2.getMonth() - d1.getMonth());

          return monthDiff >= 0 && monthDiff <= months;
        }

        cartData.forEach((cart) => {
          const cartDate = new Date(cart.created_at);
          const formattedDate = cartDate.toISOString().split("T")[0];

          cart.items.forEach((item) => {
            const { date_peremption, quantity = 0, extra } = item;
            const price = item.medicamentDetails?.prix || 0;
            const expirationDate = new Date(date_peremption);

            // Pro-périmés products
            if (isNearExpiry(cartDate, expirationDate, item.medicamentDetails?.nearExpiryMonths ?? item.nearExpiryMonths ?? 6)) {
              if (!properimeSoldPerDay[formattedDate]) {
                properimeSoldPerDay[formattedDate] = 0;
                properimeValuePerDay[formattedDate] = 0;
              }
              properimeSoldPerDay[formattedDate] += quantity;
              properimeValuePerDay[formattedDate] += quantity * price;
            }

            // Vente Extra products
            if (extra === true) {
              if (!venteExtraSoldPerDay[formattedDate]) {
                venteExtraSoldPerDay[formattedDate] = 0;
                venteExtraValuePerDay[formattedDate] = 0;
              }
              venteExtraSoldPerDay[formattedDate] += quantity;
              venteExtraValuePerDay[formattedDate] += quantity * price;
            }
          });
        });

        // Convert to arrays
        const properimeSoldArray = Object.entries(properimeSoldPerDay).map(
          ([date, quantity]) => ({
            date,
            quantity,
            value: properimeValuePerDay[date] || 0,
          })
        );

        const venteExtraSoldArray = Object.entries(venteExtraSoldPerDay).map(
          ([date, quantity]) => ({
            date,
            quantity,
            value: venteExtraValuePerDay[date] || 0,
          })
        );

        setDailyProperimeSold(properimeSoldArray);
        setProperimeCount(
          properimeSoldArray.reduce((sum, { quantity }) => sum + quantity, 0)
        );
        setProperimeValue(
          properimeSoldArray.reduce((sum, { value }) => sum + value, 0)
        );

        setDailyVenteExtraSold(venteExtraSoldArray);
        setVenteExtraCount(
          venteExtraSoldArray.reduce((sum, { quantity }) => sum + quantity, 0)
        );
        setVenteExtraValue(
          venteExtraSoldArray.reduce((sum, { value }) => sum + value, 0)
        );

        setIsLoading(false);
      } catch (error) {
        console.log("Error fetching cart data:", error);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [selectedDate]);

  // Recompute stats from cached `fullCart` when selectedOperateur or selectedDate changes.
  useEffect(() => {
    if (!fullCart || fullCart.length === 0) return;

    const computeFromCart = (cartData, operateur) => {
      const properimeSoldPerDay = {};
      const properimeValuePerDay = {};
      const venteExtraSoldPerDay = {};
      const venteExtraValuePerDay = {};

      function sixMonths(date1, date2) {
        const d1 = date1 instanceof Date ? date1 : new Date(date1);
        const d2 = date2 instanceof Date ? date2 : new Date(date2);

        const monthDiff =
          (d2.getFullYear() - d1.getFullYear()) * 12 +
          (d2.getMonth() - d1.getMonth());

        return Math.abs(monthDiff) <= 6;
      }

      cartData.forEach((cart) => {
        const isOperateurMatch =
          operateur === "all" ||
          (operateur === "admin" && !cart.code_operateur) ||
          cart.code_operateur === operateur;

        if (!isOperateurMatch) return;

        const cartDate = new Date(cart.created_at);
        const formattedDate = cartDate.toISOString().split("T")[0];

        cart.items.forEach((item) => {
          const { date_peremption, quantity = 0, extra } = item;
          const price = item.medicamentDetails?.prix || 0;
          const expirationDate = new Date(date_peremption);

          if (sixMonths(cartDate, expirationDate)) {
            if (!properimeSoldPerDay[formattedDate]) {
              properimeSoldPerDay[formattedDate] = 0;
              properimeValuePerDay[formattedDate] = 0;
            }
            properimeSoldPerDay[formattedDate] += quantity;
            properimeValuePerDay[formattedDate] += quantity * price;
          }

          if (extra === true) {
            if (!venteExtraSoldPerDay[formattedDate]) {
              venteExtraSoldPerDay[formattedDate] = 0;
              venteExtraValuePerDay[formattedDate] = 0;
            }
            venteExtraSoldPerDay[formattedDate] += quantity;
            venteExtraValuePerDay[formattedDate] += quantity * price;
          }
        });
      });

      const properimeSoldArray = Object.entries(properimeSoldPerDay).map(
        ([date, quantity]) => ({ date, quantity, value: properimeValuePerDay[date] || 0 })
      );

      const venteExtraSoldArray = Object.entries(venteExtraSoldPerDay).map(
        ([date, quantity]) => ({ date, quantity, value: venteExtraValuePerDay[date] || 0 })
      );

      setDailyProperimeSold(properimeSoldArray);
      setProperimeCount(properimeSoldArray.reduce((sum, { quantity }) => sum + quantity, 0));
      setProperimeValue(properimeSoldArray.reduce((sum, { value }) => sum + value, 0));

      setDailyVenteExtraSold(venteExtraSoldArray);
      setVenteExtraCount(venteExtraSoldArray.reduce((sum, { quantity }) => sum + quantity, 0));
      setVenteExtraValue(venteExtraSoldArray.reduce((sum, { value }) => sum + value, 0));
    };

    computeFromCart(fullCart, selectedOperateur);
  }, [fullCart, selectedOperateur, selectedDate]);

  const properimeChartData = [
    {
      name: showMonetary ? `Valeur (${CURRENCY.code})` : "Quantité",
      data: dailyProperimeSold.map((item) => ({
        x: new Date(item.date),
        y: showMonetary ? Math.round(item.value) : item.quantity,
      })),
    },
  ];

  const venteExtraChartData = [
    {
      name: showMonetary ? `Valeur (${CURRENCY.code})` : "Quantité",
      data: dailyVenteExtraSold.map((item) => ({
        x: new Date(item.date),
        y: showMonetary ? Math.round(item.value) : item.quantity,
      })),
    },
  ];

  const currentData = activeChart === "properime" ? dailyProperimeSold : dailyVenteExtraSold;
  const currentChartData = activeChart === "properime" ? properimeChartData : venteExtraChartData;
  const currentChartOptions = activeChart === "properime" ? lineChartOptionsProperime : lineChartOptionsVenteExtra;
  const currentCount = activeChart === "properime" ? properimeCount : venteExtraCount;
  const currentValue = activeChart === "properime" ? properimeValue : venteExtraValue;
  const currentTitle = activeChart === "properime" 
    ? "Produits pro-périmés vendus" 
    : "Produits vente extra vendus";
  const currentSubtitle = activeChart === "properime"
    ? "Nombre total de pro-périmés vendus"
    : "Nombre total de vente extra vendus";
  const currentIcon = activeChart === "properime" ? MdOutlineCalendarToday : MdAddCircleOutline;

  if (isLoading) {
    return (
      <Card
        justifyContent="center"
        align="center"
        direction="column"
        w="100%"
        mb="0px"
        minH="350px"
        {...rest}
      >
        <Flex justify="space-between" ps="0px" pe="20px" pt="5px" w="100%">
          <Flex align="center" w="100%">
            <Text
              me="auto"
              color={textColor}
              fontSize="xl"
              fontWeight="700"
              lineHeight="100%"
            >
              Statistiques des ventes
            </Text>
            <Icon as={MdBarChart} color={iconColor} w="24px" h="24px" />
          </Flex>
        </Flex>
        <Center h="100%" minH="280px">
          <Flex direction="column" align="center" gap="4">
            <Spinner
              thickness="4px"
              speed="0.65s"
              emptyColor="gray.200"
              color="brand.500"
              size="xl"
            />
            <Text color="secondaryGray.600" fontSize="sm" fontWeight="500">
              Chargement des données...
            </Text>
          </Flex>
        </Center>
      </Card>
    );
  }

  return (
    <Card
      justifyContent="center"
      align="center"
      direction="column"
      w="100%"
      mb="0px"
      {...rest}
    >
      <Flex justify="space-between" ps="0px" pe="20px" pt="5px" w="100%" flexWrap="wrap" gap="10px" align="center">
        <HStack spacing={2}>
          <Text
            color={textColor}
            fontSize="xl"
            fontWeight="700"
            lineHeight="100%"
          >
            {currentTitle}
          </Text>
          <Icon as={currentIcon} color={iconColor} w="24px" h="24px" />
        </HStack>
        <HStack spacing={4} flexWrap="wrap">
          {/* Toggle Buttons */}
          <ButtonGroup size="sm" isAttached variant="outline">
            <Button
              bg={activeChart === "properime" ? bgButtonActive : bgButton}
              color={activeChart === "properime" ? textButtonActive : textButton}
              borderRadius="10px"
              borderRightRadius="0"
              onClick={() => setActiveChart("properime")}
              _hover={{
                bg: activeChart === "properime" ? bgButtonActive : "secondaryGray.400",
              }}
              leftIcon={<Icon as={MdOutlineCalendarToday} />}
            >
              Pro-périmés
            </Button>
            <Button
              bg={activeChart === "venteExtra" ? bgButtonActive : bgButton}
              color={activeChart === "venteExtra" ? textButtonActive : textButton}
              borderRadius="10px"
              borderLeftRadius="0"
              onClick={() => setActiveChart("venteExtra")}
              _hover={{
                bg: activeChart === "venteExtra" ? bgButtonActive : "secondaryGray.400",
              }}
              leftIcon={<Icon as={MdAddCircleOutline} />}
            >
              Vente Extra
            </Button>
          </ButtonGroup>
          <FormControl display="flex" alignItems="center" w="auto">
            <FormLabel htmlFor="monetary-switch" mb="0" fontSize="sm" color="secondaryGray.600">
              Quantité
            </FormLabel>
            <Switch
              id="monetary-switch"
              colorScheme="brand"
              isChecked={showMonetary}
              onChange={() => setShowMonetary(!showMonetary)}
            />
            <FormLabel htmlFor="monetary-switch" mb="0" ml="2" fontSize="sm" color="secondaryGray.600">
              {CURRENCY.code}
            </FormLabel>
          </FormControl>
        </HStack>
      </Flex>

      <Flex w="100%" flexDirection={{ base: "column", lg: "row" }}>
        <Flex flexDirection="column" me="20px" mt="28px">
          <Text
            color={textColor}
            fontSize="34px"
            textAlign="start"
            fontWeight="700"
            lineHeight="100%"
          >
            {showMonetary ? `${Math.round(currentValue)} ${CURRENCY.code}` : currentCount}
          </Text>
          <Flex align="center" mb="20px">
            <Text
              color="secondaryGray.600"
              fontSize="sm"
              fontWeight="500"
              mt="4px"
              me="12px"
            >
              {showMonetary 
                ? `Valeur totale en ${CURRENCY.code}` 
                : currentSubtitle}
            </Text>
            <Flex align="center">
              <Icon as={RiArrowUpSFill} color="green.500" me="2px" mt="2px" />
            </Flex>
          </Flex>
        </Flex>
        <Box minH="260px" minW="75%" mt="auto">
          {currentData.length > 0 && (
            <LineChart
              chartData={currentChartData}
              chartOptions={currentChartOptions}
            />
          )}
        </Box>
      </Flex>
      <Flex justify="center" mt="10px" gap="10px" flexWrap="wrap">
        {isLoadingOperators ? (
          <Spinner size="md" />
        ) : (
          <>
            <Button
              onClick={() => setSelectedOperateur("all")}
              bg={selectedOperateur === "all" ? "brand.500" : "gray.300"}
              color={selectedOperateur === "all" ? "white" : "gray.700"}
              _hover={{ bg: "brand.600" }}
              minW="auto"
              px="20px"
              mb="5px"
            >
              All
            </Button>
            {uniqueOperateurs
              .filter((operateur) => operatorNames[operateur]) // Don't show deleted profiles
              .map((operateur) => (
                <Button
                  key={operateur}
                  onClick={() => setSelectedOperateur(operateur)}
                  bg={selectedOperateur === operateur ? "brand.500" : "gray.300"}
                  color={selectedOperateur === operateur ? "white" : "gray.700"}
                  _hover={{ bg: "brand.600" }}
                  minW="auto"
                  px="20px"
                  mb="5px"
                >
                  {operatorNames[operateur]}
                </Button>
              ))}
          </>
        )}
      </Flex>
    </Card>
  );
}
